import { lazy, Suspense, type ReactNode } from 'react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { Button } from '@worldcoo/ui/components/button';
import { CircleArrowDownIcon } from 'lucide-react';
import { Statistics } from './statistics';
import { useLingui } from '@lingui/react/macro';
import { Image } from '../../-components/image';
import { usePortalMetadataQuery } from '@/lib/data-hooks';
import { cn, heroImageSize } from '@worldcoo/ui/lib/utils';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { isNotFound } from '@tanstack/react-router';
import { usePortalDisplayContext } from './portal-display-context';

const NotTranslatedInfo = lazy(() => import('./not-translated-info').then((m) => ({ default: m.NotTranslatedInfo })));

export function Hero() {
	const portalData = usePortalDisplayContext();
	if (portalData) {
		return <DisplayContextHero />;
	}

	return (
		<Suspense
			fallback={
				<div className="flex flex-col items-center px-6 py-20 lg:flex-row">
					<section className="flex w-full flex-col gap-6 px-6 py-10 lg:w-1/2">
						<Skeleton className="bg-secondary h-9 w-32" />

						<div className="flex flex-col gap-1">
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<br />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<br />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<Skeleton className="bg-secondary h-6 w-sm" />
						</div>

						<Skeleton className="bg-secondary h-14 w-48" />
					</section>

					<div className="w-full max-w-[720px] px-6 py-20 sm:p-10 lg:w-1/2">
						<Skeleton className={cn('bg-secondary h-full w-full rounded-2xl', heroImageSize.className)} />
					</div>
				</div>
			}
		>
			<SuspensedHero />
		</Suspense>
	);
}

function DisplayContextHero() {
	const portalData = usePortalDisplayContext()!;
	const { t } = useLingui();
	return (
		<HeroContent
			title={portalData.title}
			presentation={portalData.presentation}
			textsNotFound={portalData.textsNotFound}
			heroButton={t`Start donating`}
			image={
				portalData.coverImageUrl ? (
					<img
						src={portalData.coverImageUrl}
						className={cn('bg-background w-full rounded-2xl object-cover', heroImageSize.className)}
						alt={portalData.imageAlt ?? 'hero-image'}
					/>
				) : (
					<div
						className={cn('bg-secondary w-full rounded-2xl', heroImageSize.className)}
						style={{ aspectRatio: heroImageSize.width / heroImageSize.height }}
					/>
				)
			}
		/>
	);
}

function SuspensedHero() {
	const { t } = useLingui();
	const metadata = usePortalMetadataQuery();

	if (isNotFound(metadata)) return null;

	return (
		<HeroContent
			title={metadata.texts.title}
			presentation={metadata.texts.presentation}
			textsNotFound={metadata.textsNotFound}
			heroButton={metadata.texts.donateButton ?? t`Start donating`}
			onButtonClick={() => document.getElementById('campaigns')?.scrollIntoView({ behavior: 'smooth' })}
			image={
				<Image
					src={metadata.media?.location}
					height={heroImageSize.height}
					aspectRatio={heroImageSize.width / heroImageSize.height}
					className={cn('bg-background w-full rounded-2xl object-cover', heroImageSize.className)}
					alt={metadata.imageAlt ?? 'hero-image'}
					priority
				/>
			}
		/>
	);
}

function HeroContent(props: {
	title?: string;
	presentation?: string;
	textsNotFound: boolean;
	heroButton: string;
	onButtonClick?: () => void;
	image: ReactNode;
}) {
	return (
		<DivWithPrimaryColorVariable className="flex flex-col items-center gap-12 p-6 lg:flex-row lg:gap-20 lg:py-20">
			<section className="flex w-full flex-1 flex-col lg:w-1/2">
				{props.textsNotFound && (
					<Suspense>
						<NotTranslatedInfo className="mb-4" />
					</Suspense>
				)}

				<h1 className="text-[32px] font-medium lg:text-[40px]">{props.title}</h1>

				{props.presentation && (
					<div
						className="unreset-tailwind my-4"
						dangerouslySetInnerHTML={{
							__html: props.presentation,
						}}
					/>
				)}

				<Button
					size="2xl"
					className="cursor-pointer sm:w-fit"
					onClick={props.onButtonClick}
					style={{ backgroundColor: 'var(--primary)' }}
				>
					{props.heroButton}
					<CircleArrowDownIcon strokeWidth={1.25} />
				</Button>
			</section>

			<div className="relative mx-auto w-full max-w-[630px] lg:mx-0 lg:w-1/2">
				<div className="relative">
					{props.image}
					<div className="bg-primary absolute inset-0 -z-10 rotate-3 rounded-lg lg:rotate-5" />
				</div>

				<div className="relative -mt-20 w-full p-1.5 lg:absolute lg:inset-x-0 lg:bottom-0 lg:mt-0 lg:p-5">
					<Statistics />
				</div>
			</div>
		</DivWithPrimaryColorVariable>
	);
}
